import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/seo";
import { FiDollarSign, FiChevronDown, FiChevronUp } from "@/lib/icons";
import { ToggleGroup } from "./_components/ToggleGroup";
import { useRepaymentCalculator, type RepaymentFrequency } from "./_hooks/useRepaymentCalculator";
import { cn } from "@/lib/utils";

/**
 * Format as AUD currency
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Slider input component
 */
function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  formatValue,
  presets,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  formatValue: (v: number) => string;
  presets?: { value: number; label: string }[];
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <span className="text-xl font-bold text-primary">{formatValue(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
      />
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{formatValue(min)}</span>
        <span>{formatValue(max)}</span>
      </div>
      {presets && (
        <div className="flex flex-wrap gap-2 pt-2">
          {presets.map((preset) => (
            <button
              key={preset.value}
              onClick={() => onChange(preset.value)}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-lg transition-colors",
                value === preset.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Repayment Calculator Page
 */
export function RepaymentCalculatorPage() {
  const { inputs, results, amortization, updateInput } = useRepaymentCalculator();
  const [showAmortization, setShowAmortization] = useState(false);

  const frequencyOptions: { value: RepaymentFrequency; label: string }[] = [
    { value: "weekly", label: "Weekly" },
    { value: "fortnightly", label: "Fortnightly" },
    { value: "monthly", label: "Monthly" },
  ];

  const loanPresets = [
    { value: 300000, label: "$300K" },
    { value: 500000, label: "$500K" },
    { value: 750000, label: "$750K" },
    { value: 1000000, label: "$1M" },
  ];

  const termPresets = [
    { value: 15, label: "15 yrs" },
    { value: 20, label: "20 yrs" },
    { value: 25, label: "25 yrs" },
    { value: 30, label: "30 yrs" },
  ];

  const frequencyLabel =
    inputs.frequency === "weekly"
      ? "week"
      : inputs.frequency === "fortnightly"
      ? "fortnight"
      : "month";

  return (
    <>
      <SEO
        title="Repayment Calculator"
        description="Calculate your home loan repayments based on loan amount, interest rate, and loan term. See weekly, fortnightly, or monthly payments."
        url="/calculators/repayments"
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left - Info */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-accent/20 rounded-xl">
                  <FiDollarSign className="size-6 text-primary" />
                </div>
              </div>

              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
                Repayment Calculator
              </h1>

              <p className="text-lg text-muted-foreground mb-8 max-w-md">
                Calculate your estimated home loan repayments and see how different loan amounts,
                interest rates, and terms affect your payments.
              </p>

              <div className="border-t border-border pt-8 mt-8">
                <h2 className="font-serif text-2xl mb-4">
                  Want a more accurate estimate?
                </h2>
                <p className="text-muted-foreground mb-6">
                  Our team can provide personalised calculations based on your exact situation
                  and current lender offers.
                </p>
                <Button asChild className="btn-pill bg-accent text-accent-foreground hover:bg-accent/90">
                  <Link to="/contact">Get a Quote</Link>
                </Button>
              </div>
            </div>

            {/* Right - Calculator */}
            <div className="bg-background rounded-3xl shadow-lg border border-border p-8">
              <h2 className="font-serif text-xl mb-8">Enter your loan details</h2>

              <div className="space-y-8">
                {/* Loan Amount */}
                <SliderInput
                  label="Loan Amount"
                  value={inputs.loanAmount}
                  onChange={(v) => updateInput("loanAmount", v)}
                  min={50000}
                  max={2000000}
                  step={10000}
                  formatValue={formatCurrency}
                  presets={loanPresets}
                />

                {/* Interest Rate */}
                <SliderInput
                  label="Interest Rate (p.a.)"
                  value={inputs.interestRate}
                  onChange={(v) => updateInput("interestRate", v)}
                  min={2}
                  max={10}
                  step={0.05}
                  formatValue={(v) => `${v.toFixed(2)}%`}
                />

                {/* Loan Term */}
                <SliderInput
                  label="Loan Term"
                  value={inputs.loanTerm}
                  onChange={(v) => updateInput("loanTerm", v)}
                  min={5}
                  max={30}
                  step={1}
                  formatValue={(v) => `${v} years`}
                  presets={termPresets}
                />

                {/* Frequency */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Repayment Frequency</label>
                  <ToggleGroup
                    options={frequencyOptions}
                    value={inputs.frequency}
                    onChange={(v) => updateInput("frequency", v)}
                  />
                </div>
              </div>

              {/* Results */}
              <div className="mt-10 pt-8 border-t border-border">
                <div className="bg-primary text-primary-foreground rounded-2xl p-6 mb-6">
                  <p className="text-primary-foreground/90 text-sm mb-1">
                    Your {inputs.frequency} repayment
                  </p>
                  <p className="text-4xl font-bold">{formatCurrency(results.repaymentAmount)}</p>
                  <p className="text-primary-foreground/90 text-sm mt-2">per {frequencyLabel}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-cream rounded-xl p-4">
                    <p className="text-sm text-muted-foreground mb-1">Total Repayments</p>
                    <p className="text-xl font-bold">{formatCurrency(results.totalRepayments)}</p>
                  </div>
                  <div className="bg-cream rounded-xl p-4">
                    <p className="text-sm text-muted-foreground mb-1">Total Interest</p>
                    <p className="text-xl font-bold text-muted-foreground">
                      {formatCurrency(results.totalInterest)}
                    </p>
                  </div>
                </div>

                {/* Interest vs Principal Visual */}
                <div className="mt-6">
                  <p className="text-sm text-muted-foreground mb-2">Principal vs Interest</p>
                  <div className="h-4 rounded-full overflow-hidden flex bg-muted">
                    <div
                      className="bg-primary transition-all duration-500"
                      style={{
                        width: `${(inputs.loanAmount / results.totalRepayments) * 100}%`,
                      }}
                    />
                    <div
                      className="bg-accent transition-all duration-500"
                      style={{
                        width: `${(results.totalInterest / results.totalRepayments) * 100}%`,
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-2">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-primary" />
                      Principal ({((inputs.loanAmount / results.totalRepayments) * 100).toFixed(0)}%)
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-accent" />
                      Interest ({((results.totalInterest / results.totalRepayments) * 100).toFixed(0)}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amortization Schedule */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <button
            onClick={() => setShowAmortization(!showAmortization)}
            className="flex items-center gap-2 text-lg font-medium mb-6 hover:text-primary transition-colors"
          >
            {showAmortization ? <FiChevronUp /> : <FiChevronDown />}
            {showAmortization ? "Hide" : "Show"} Yearly Breakdown
          </button>

          {showAmortization && (
            <div className="bg-background rounded-2xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left p-4 font-medium">Year</th>
                      <th className="text-right p-4 font-medium">Annual Payment</th>
                      <th className="text-right p-4 font-medium">Principal</th>
                      <th className="text-right p-4 font-medium">Interest</th>
                      <th className="text-right p-4 font-medium">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {amortization.map((row) => (
                      <tr key={row.period} className="border-t border-border">
                        <td className="p-4 font-medium">{row.period}</td>
                        <td className="p-4 text-right">{formatCurrency(row.payment)}</td>
                        <td className="p-4 text-right text-primary">{formatCurrency(row.principal)}</td>
                        <td className="p-4 text-right text-accent">{formatCurrency(row.interest)}</td>
                        <td className="p-4 text-right font-medium">{formatCurrency(row.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-6">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> This calculator provides estimates only and does not constitute
              financial advice. Actual repayments may vary based on lender fees, loan features, and
              other factors. Contact us for an accurate quote based on current lender rates.
            </p>
            <p className="text-sm text-muted-foreground">
              <strong>Disclaimer:</strong> This page provides general information only and has been
              prepared without considering your objectives, financial situation or needs. We recommend
              that you consider whether it is appropriate for your circumstances and your full financial
              situation will need to be reviewed prior to acceptance of any offer or product. It does
              not constitute legal, tax or financial advice and you should always seek professional
              advice in relation to your individual circumstances.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
